import {
  type AuthErrorLike,
  continueMode,
  type EmailCodeSignInClient,
  emailContinue,
  type EmailContinueClient,
  type EmailContinueOutcome,
  forgotPasswordAvailable,
  mapAuthError,
  MIN_PASSWORD_LENGTH,
  requestSignInCode,
  signInWithCode,
} from '@flashka/shared/auth';
import { useResendCooldown } from '@flashka/shared/react/use-resend-cooldown';
import {
  BadgedButtonChip,
  Button,
  CodeInput,
  CodeStepLayout,
  DialogBack,
  DialogSurface,
  DialogTopLeft,
  Input,
  LegalConsentLine,
  LegalConsentLink,
  SIGN_IN_ACTION_SIZE,
  SIGN_IN_FIELD_SIZE,
  SIGN_IN_KA_TILE_SIZE,
  SignInHeader,
  SignInLayout,
  SignInWaitNotice,
} from '@flashka/shared/ui';

import { type FormEvent, type KeyboardEvent, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { KaFloatingMascot } from '@/components/rive/ka-floating-mascot';

import { authClient, resetLocalCredentials } from '../client';
import { getLastLoginMethod, rememberLoginMethod } from '../last-login-method';
import { useAuthAction } from '../use-auth-action';
import { SocialButtons } from './social-buttons';

type Step = 'form' | 'code-sign-in' | 'forgot-reset' | 'verify-email';

interface AuthCardProps {
  onAuthenticated?: () => void;
  /**
   * App path the Google/Apple round-trip returns to (success AND failure).
   * Defaults to the current pathname, i.e. come back to where the card is.
   * The modal passes its post-auth destination here, because no client code
   * runs after an OAuth redirect — `onAuthenticated` never fires for it.
   */
  callbackPath?: string;
  /**
   * 'card' (default) draws its own bordered card — the inline /sign-in
   * page and stories. 'plain' skips the chrome so a host DialogContent can
   * provide the standard dialog border/background/padding instead of
   * clipping a card-in-a-card.
   */
  variant?: 'card' | 'plain';
}

// The casts are structural: each shared flow drives the slice of the client
// it declares, which authClient satisfies.
const continueClient = authClient as unknown as EmailContinueClient;
const codeClient = authClient as unknown as EmailCodeSignInClient;

// The shared code flows pass the client's own result straight through, so
// their `error` is OPTIONAL; `run` wants it settled to null on success (the
// same normalization the emailContinue call site does inline below).
async function settled(result: Promise<{ error?: AuthErrorLike }>) {
  return { error: (await result).error ?? null };
}

export function AuthCard({
  onAuthenticated,
  callbackPath,
  variant = 'card',
}: AuthCardProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('form');
  // Read once on mount — it only changes as a side effect of signing in,
  // which unmounts the card.
  const [lastUsed] = useState(getLastLoginMethod);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  // A failed OAuth round-trip redirects back with ?error=<code> (the
  // errorCallbackURL passed to signIn.social); surface it as the card's
  // initial error and scrub the param so a refresh doesn't resurrect it.
  const {
    busy: requestBusy,
    errorKey,
    run,
    setErrorKey,
    wait,
  } = useAuthAction(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthError = params.get('error');
    if (!oauthError) return null;
    params.delete('error');
    const query = params.toString();
    window.history.replaceState(
      null,
      '',
      window.location.pathname + (query ? `?${query}` : ''),
    );
    return mapAuthError({ code: oauthError.toUpperCase() });
  });
  // Signed in: the CTA keeps its spinner until the card goes away. The
  // session lands a round trip after the sign-in call resolves; letting the
  // button flick back to "Continue" (or the page show a second sign-in card)
  // in between read as a failed attempt.
  const [finishing, setFinishing] = useState(false);
  const busy = requestBusy || finishing;
  function finish() {
    setFinishing(true);
    onAuthenticated?.();
  }
  const resend = useResendCooldown();
  // ONE Continue, two jobs, decided by the password field alone (shared
  // predicate, same on mobile): typed → password sign-in-or-sign-up; empty →
  // a sign-in code is emailed. The button's label announces which, so leaving
  // the password blank never sends an email by surprise.
  const mode = continueMode(password);
  const passwordRef = useRef<HTMLInputElement>(null);

  // Enter is the card's own, not left to the browser's implicit submission
  // (which a disabled default button or a password manager's fill can quietly
  // swallow): Enter in the email field moves on to the password — it never
  // sends, so an address alone is never emailed a code by surprise — and
  // Enter in the password field is Continue. Same chain as the app's sign-in
  // sheet (AuthSheetBody's Return keys). An IME composing text keeps Enter.
  function isPlainEnter(e: KeyboardEvent<HTMLInputElement>) {
    return e.key === 'Enter' && !e.nativeEvent.isComposing;
  }
  function onEmailKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!isPlainEnter(e)) return;
    e.preventDefault();
    passwordRef.current?.focus();
  }
  function onPasswordKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!isPlainEnter(e)) return;
    e.preventDefault();
    if (!busy && email) e.currentTarget.form?.requestSubmit();
  }

  function reset(next: Step) {
    setOtp('');
    setNewPassword('');
    setErrorKey(null);
    setStep(next);
  }

  // ── continue: sign in, or fall back to sign-up + email verification ──
  // ONE flow for new and existing users, shared with mobile (`emailContinue`
  // in @flashka/shared/auth). The worker's anti-enumeration sign-up answers
  // an existing email with a synthetic success — and emails that user a code
  // via onExistingUserSignUp — so this card cannot know whether an account
  // was created: the verify step says "we sent a code", never "account
  // created".
  async function submitContinue(e: FormEvent) {
    e.preventDefault();
    if (mode === 'code') {
      if (await sendSignInCode()) reset('code-sign-in');
      return;
    }
    resetLocalCredentials(); // fresh credentials must outrank any planted Bearer
    let outcome: EmailContinueOutcome | undefined;
    const ok = await run(async () => {
      const result = await emailContinue(continueClient, email, password);
      outcome = result.outcome;
      return { error: result.error ?? null };
    });
    if (!ok) return;
    if (outcome === 'signed-in') {
      rememberLoginMethod('email');
      finish();
      return;
    }
    // needs-otp / needs-verification: a code is on its way either way —
    // fresh sign-up, existing account, and abandoned-unverified all get one.
    resend.start();
    reset('verify-email');
  }

  // ── sign-in with an emailed code (no password) ─────────────────────
  // Shared by Continue (which then opens the step) and the step's own Resend
  // (which, like the verify step's, leaves any typed digits alone).
  async function sendSignInCode(): Promise<boolean> {
    const ok = await run(() => settled(requestSignInCode(codeClient, email)));
    if (ok) resend.start();
    return ok;
  }

  // `code` is passed by the auto-submit on the 6th digit, so the value used
  // never depends on whether the OTP input reports completion before or after
  // `otp` state has re-rendered.
  async function submitCodeSignIn(code = otp) {
    // signIn.emailOtp mints a session — nothing of a previous one may survive it
    resetLocalCredentials();
    if (await run(() => settled(signInWithCode(codeClient, email, code)))) {
      rememberLoginMethod('email');
      finish();
      return;
    }
    // Wrong or expired: clear the digits so the user types a fresh code. Left
    // in place, the enabled button would resubmit the SAME code and burn one
    // of the worker's three attempts for nothing.
    setOtp('');
  }

  // ── forgot password ────────────────────────────────────────────────
  async function sendResetOtp() {
    if (await run(() => authClient.emailOtp.requestPasswordReset({ email }))) {
      resend.start();
      reset('forgot-reset');
    }
  }

  async function submitReset() {
    // Check BEFORE calling resetPassword: the endpoint verifies (and
    // consumes) the OTP before validating password length, so a too-short
    // password here would burn a correct code and leave the user stuck.
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setErrorKey('auth.errors.weakPassword');
      return;
    }
    resetLocalCredentials();
    const ok = await run(() =>
      authClient.emailOtp.resetPassword({ email, otp, password: newPassword }),
    );
    // resetPassword failed — stay on forgot-reset; run() already set errorKey.
    if (!ok) return;

    // resetPassword doesn't create a session, so sign in with the new
    // password. If THIS fails, the password change already succeeded — don't
    // strand the user on forgot-reset with a now-consumed OTP and an error
    // that reads as "reset failed" when it didn't. Drop back to the password
    // sign-in form with the mapped error shown there instead; they can sign
    // in manually with the new password.
    if (!(await run(() => authClient.signIn.email({ email, password: newPassword })))) {
      setOtp('');
      setNewPassword('');
      setStep('form'); // run() already set errorKey
      return;
    }
    rememberLoginMethod('email');
    finish();
  }

  async function submitVerifyEmail(code = otp) {
    // verifyEmail creates a session — nothing of a previous one may survive it
    resetLocalCredentials();
    // autoSignInAfterVerification: a session is created on success.
    if (await run(() => authClient.emailOtp.verifyEmail({ email, otp: code }))) {
      rememberLoginMethod('email');
      finish();
      return;
    }
    setOtp(''); // same reason as submitCodeSignIn: never resubmit a burnt code
  }

  async function resendVerifyEmail() {
    if (
      await run(() =>
        authClient.emailOtp.sendVerificationOtp({ email, type: 'email-verification' }),
      )
    ) {
      resend.start();
    }
  }

  // The three code steps share one form. `resendCode` re-requests the SAME
  // kind of code the step was entered with — a sign-in code cannot verify a
  // sign-up, and vice versa. Code-only steps submit themselves on the 6th
  // digit (typed, pasted or autofilled) — the button stays for keyboard and
  // assistive-tech users; the reset step also needs a new password, so it
  // waits for the button.
  function describeCodeStep() {
    switch (step) {
      case 'code-sign-in':
        return {
          title: t('auth.signIn.title'),
          action: t('auth.buttons.signIn'),
          submit: submitCodeSignIn,
          resendCode: sendSignInCode,
          needsNewPassword: false,
        };
      case 'verify-email':
        return {
          title: t('auth.verifyEmail.title'),
          action: t('auth.otp.verify'),
          submit: submitVerifyEmail,
          resendCode: resendVerifyEmail,
          needsNewPassword: false,
        };
      case 'forgot-reset':
        return {
          title: t('auth.forgot.title'),
          action: t('auth.forgot.action'),
          submit: submitReset,
          resendCode: sendResetOtp,
          needsNewPassword: true,
        };
      default:
        return null;
    }
  }
  const codeStep = describeCodeStep();

  // The whole card sits inside ONE <form>, social buttons included: every
  // Pressable defaults to type="button", so only the CTA submits. Enter in the
  // two fields is handled explicitly (onEmailKeyDown / onPasswordKeyDown).
  const body = (
    <>
      {step === 'form' && (
        <form onSubmit={submitContinue}>
          <SignInLayout
            header={
              <SignInHeader
                title={t('auth.continue.title')}
                subtitle={t('auth.continue.subtitle')}
                ka={<KaFloatingMascot size={SIGN_IN_KA_TILE_SIZE} />}
              />
            }
            social={
              <SocialButtons
                // Absolute, not relative: the auth worker resolves this URL
                // against its OWN origin, not the app's — a bare pathname would
                // redirect post-OAuth back to the worker instead of the app.
                callbackURL={
                  window.location.origin + (callbackPath ?? window.location.pathname)
                }
                disabled={busy}
                // Social failures land in the same error line the email flows
                // use, rather than vanishing.
                onError={setErrorKey}
                // …and a rate-limit wait in the same countdown line.
                rateLimitWait={wait}
              />
            }
            dividerLabel={t('auth.social.divider')}
            fields={
              <>
                <Input
                  size={SIGN_IN_FIELD_SIZE}
                  label={t('auth.fields.email')}
                  // "Last used" for email marks the FIELD, never the CTA: the
                  // CTA is disabled until an address is typed, and "Forgot
                  // password?" appears right above it. Same chip as the social
                  // buttons (mobile marks its email field the same way).
                  labelTrailing={
                    lastUsed === 'email' ? (
                      <BadgedButtonChip label={t('auth.social.lastUsed')} />
                    ) : undefined
                  }
                  value={email}
                  onChangeText={setEmail}
                  webProps={{
                    type: 'email',
                    name: 'email',
                    autoComplete: 'email',
                    required: true,
                    onKeyDown: onEmailKeyDown,
                  }}
                />
                {/* Not `required`: an empty password is the emailed-code path,
                    see `mode` above. */}
                <Input
                  ref={passwordRef}
                  size={SIGN_IN_FIELD_SIZE}
                  label={t('auth.fields.password')}
                  value={password}
                  onChangeText={setPassword}
                  webProps={{
                    type: 'password',
                    name: 'password',
                    autoComplete: 'current-password',
                    onKeyDown: onPasswordKeyDown,
                  }}
                />
              </>
            }
            // Absent until there is an address to send a code to — the shared
            // predicate mobile reads too.
            forgotPassword={
              forgotPasswordAvailable(email)
                ? {
                    label: t('auth.signIn.forgotPassword'),
                    onPress: () => void sendResetOtp(),
                    disabled: busy,
                  }
                : undefined
            }
            action={
              <Button
                variant="primary"
                size={SIGN_IN_ACTION_SIZE}
                fullWidth
                loading={busy}
                disabled={busy || !email}
                webProps={{ type: 'submit' }}>
                {mode === 'code'
                  ? t('auth.continue.sendCode')
                  : t('auth.continue.action')}
              </Button>
            }
            /* Under the whole card, not just the form: Continue AND the social
               buttons above can create an account, and the same sentence has
               to cover both. Paths are the app's own routes — the anchor opens
               a new tab so what was typed here survives. */
            footer={
              <LegalConsentLine>
                <Trans
                  i18nKey="legal.continueNotice"
                  components={{
                    terms: <LegalConsentLink href="/terms" />,
                    privacy: <LegalConsentLink href="/privacy" />,
                  }}
                />
              </LegalConsentLine>
            }
          />
        </form>
      )}

      {codeStep && (
        <>
          {/* Back is the card's top-left icon chip now, not a text link under
              the CTA (Figma 30000:39591). It renders from INSIDE the card so
              both hosts get it — the composed `Dialog` in auth-dialogs and the
              local `DialogSurface` below — and it pins correctly in both: the
              shared View reset deliberately omits `position: relative`, so the
              nearest positioned ancestor is the dialog PANEL either way.
              `secondary`, not ghost: Simo, 2026-09-09. */}
          <DialogTopLeft>
            <DialogBack
              variant="secondary"
              accessibilityLabel={t('auth.otp.back')}
              onPress={() => reset('form')}
            />
          </DialogTopLeft>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void codeStep.submit();
            }}>
            {/* The shared block owns the order AND the rhythm of all three
                code steps (header → 36 → cells → [extra, 16] → 36 → CTA → 8 →
                resend), including the resend line's two states — so the form
                adds no layout classes of its own. */}
            <CodeStepLayout
              header={
                <SignInHeader
                  title={codeStep.title}
                  subtitle={t('auth.otp.sentTo', { email })}
                  ka={<KaFloatingMascot size={SIGN_IN_KA_TILE_SIZE} />}
                />
              }
              input={
                <CodeInput
                  value={otp}
                  onChangeText={setOtp}
                  onComplete={
                    codeStep.needsNewPassword || busy
                      ? undefined
                      : (code) => void codeStep.submit(code)
                  }
                  accessibilityLabel={t('auth.fields.code')}
                  autoFocus
                />
              }
              extra={
                codeStep.needsNewPassword ? (
                  <Input
                    size={SIGN_IN_FIELD_SIZE}
                    label={t('auth.fields.newPassword')}
                    value={newPassword}
                    onChangeText={setNewPassword}
                    webProps={{
                      type: 'password',
                      name: 'new-password',
                      autoComplete: 'new-password',
                      required: true,
                    }}
                  />
                ) : undefined
              }
              /* xl on every step, like the form's CTA: the card must not
                 change height under the user when it swaps a step in. */
              action={
                <Button
                  variant="primary"
                  size={SIGN_IN_ACTION_SIZE}
                  fullWidth
                  loading={busy}
                  disabled={
                    busy || otp.length < 6 || (codeStep.needsNewPassword && !newPassword)
                  }
                  webProps={{ type: 'submit' }}>
                  {codeStep.action}
                </Button>
              }
              resend={{
                label: resend.ready
                  ? t('auth.otp.resend')
                  : t('auth.otp.resendIn', { seconds: resend.remaining }),
                onPress: () => void codeStep.resendCode(),
                ready: resend.ready,
                disabled: busy,
              }}
            />
          </form>
        </>
      )}

      {/* Somebody else's sign-ins share this one's rate limit (every browser
          reaches the worker through the same proxy IPs), so a 429 is waited
          out and retried by `run` — said here, so a long spinner is never a
          mystery. Not an error: nothing has failed yet. */}
      {wait.seconds > 0 ? (
        <SignInWaitNotice
          className="mt-3 block"
          label={t('auth.errors.rateLimitedRetrying', { seconds: wait.seconds })}
        />
      ) : errorKey ? (
        <p role="alert" className="mt-3 text-center text-sm text-destructive">
          {t(errorKey)}
        </p>
      ) : null}
    </>
  );

  // 'plain' — the host DialogContent is already the DS panel, so adding a
  // second one here would clip a card inside a card.
  if (variant === 'plain') return <div className="w-full">{body}</div>;
  // 'card' — the inline /sign-in page renders the SAME panel the dialog does
  // (DS Dialog `md`, hugging its content). Composed mode, so no DS title row
  // and no ✕: the page has nothing to close back to.
  return (
    <DialogSurface size="md" hug showClose={false}>
      {body}
    </DialogSurface>
  );
}
